// computeBlend: two distilled profiles -> BlendResult (§9).
// Pure function over the two `films` maps, canonicalized to the (title, year)
// key. All blend math lives here so it can be hand-checked against fixtures.

import { canonicalizeFilms } from "./distiller.ts";
import type {
  BlendResult,
  BlendTier,
  CrossRecItem,
  DisputeItem,
  DistilledProfile,
  FilmEntry,
  SharedCanonItem,
  WatchTonightItem,
} from "./types.ts";

const watched = (e: FilmEntry) => e.s.includes("w");
const watchlisted = (e: FilmEntry) => e.s.includes("l");
const rated = (e: FilmEntry) => e.s.includes("r") && typeof e.r === "number";

const displayName = (a: FilmEntry, b?: FilmEntry) => (a.t || b?.t || "").trim();
const yearOf = (a: FilmEntry, b?: FilmEntry) => a.y ?? b?.y;

function tierFor(score: number): BlendTier {
  if (score >= 85) return "twin flames";
  if (score >= 70) return "great double bill";
  if (score >= 50) return "opposites with overlap";
  return "chaos pairing";
}

function meanRating(films: Record<string, FilmEntry>): number {
  let sum = 0;
  let n = 0;
  for (const e of Object.values(films)) {
    if (rated(e)) {
      sum += e.r as number;
      n++;
    }
  }
  return n === 0 ? 0 : sum / n;
}

export function computeBlend(
  profileA: DistilledProfile,
  profileB: DistilledProfile,
): BlendResult {
  // Canonicalize onto the (title, year) key. Fresh profiles are already keyed
  // this way; older stored profiles were URI-keyed and can hold the same film
  // twice (film URI vs diary-log URI) — this collapses those so counts, canon,
  // disputes, and cross-recs never double-count a movie.
  const A = canonicalizeFilms(profileA.films);
  const B = canonicalizeFilms(profileB.films);

  const bothRated: DisputeItem[] = [];
  const sharedCanon: SharedCanonItem[] = [];
  const watchTonight: WatchTonightItem[] = [];

  let bothWatchedCount = 0;
  let bothWatchlistedCount = 0;

  // Union of watched films for Jaccard.
  const watchedUnion = new Set<string>();
  let onlyAWatched = 0;
  let onlyBWatched = 0;

  for (const [uri, a] of Object.entries(A)) {
    if (watched(a)) watchedUnion.add(uri);
  }
  for (const [uri, b] of Object.entries(B)) {
    if (watched(b)) watchedUnion.add(uri);
  }

  for (const uri of watchedUnion) {
    const a = A[uri];
    const b = B[uri];
    const aw = a && watched(a);
    const bw = b && watched(b);
    if (aw && bw) bothWatchedCount++;
    else if (aw) onlyAWatched++;
    else if (bw) onlyBWatched++;
  }

  // Walk the shared keyspace once for rated/watchlist intersections.
  for (const [uri, a] of Object.entries(A)) {
    const b = B[uri];
    if (!b) continue;

    if (watchlisted(a) && watchlisted(b)) {
      bothWatchlistedCount++;
      watchTonight.push({ uri, title: displayName(a, b), year: yearOf(a, b) });
    }

    if (rated(a) && rated(b)) {
      const ratingA = a.r as number;
      const ratingB = b.r as number;
      const gap = Math.abs(ratingA - ratingB);
      bothRated.push({ uri, title: displayName(a, b), year: yearOf(a, b), ratingA, ratingB, gap });
      if (ratingA >= 4 && ratingB >= 4) {
        sharedCanon.push({
          uri,
          title: displayName(a, b),
          year: yearOf(a, b),
          ratingA,
          ratingB,
          combined: ratingA + ratingB,
        });
      }
    }
  }

  // --- Compatibility score (§9) ---
  const bothRatedCount = bothRated.length;
  const earlyRead = bothRatedCount < 10;

  // Taste agreement (70%): 1 - mean(|rA - rB|) / 4.5 over bothRated.
  let tasteTerm = 0.5;
  if (bothRatedCount > 0) {
    const meanGap = bothRated.reduce((s, d) => s + d.gap, 0) / bothRatedCount;
    tasteTerm = 1 - meanGap / 4.5;
  }
  // Dampen toward 0.5 when we have too few shared ratings.
  if (earlyRead) {
    const confidence = bothRatedCount / 10; // 0..1
    tasteTerm = 0.5 + (tasteTerm - 0.5) * confidence;
  }

  // Overlap (30%): Jaccard through min(1, j / 0.25).
  const unionWatched = watchedUnion.size;
  const jaccard = unionWatched === 0 ? 0 : bothWatchedCount / unionWatched;
  const overlapTerm = Math.min(1, jaccard / 0.25);

  const score = Math.round((tasteTerm * 0.7 + overlapTerm * 0.3) * 100);
  const clampedScore = Math.max(0, Math.min(100, score));

  // --- Sorted output sections ---
  bothRated.sort((x, y) => y.gap - x.gap || cmpKey(x, y));
  const disputes = bothRated.filter((d) => d.gap > 0).slice(0, 10);

  sharedCanon.sort((x, y) => y.combined - x.combined || cmpKey(x, y));

  watchTonight.sort((x, y) => (x.title < y.title ? -1 : x.title > y.title ? 1 : 0));

  // Cross-recs: A rated >=4, B hasn't watched (badge if on B's watchlist).
  const crossRecsAToB = crossRecs(A, B).slice(0, 12);
  const crossRecsBToA = crossRecs(B, A).slice(0, 12);

  const averageA = meanRating(A);
  const averageB = meanRating(B);
  let harsherCritic: "A" | "B" | null = null;
  if (averageA > 0 && averageB > 0 && averageA !== averageB) {
    harsherCritic = averageA < averageB ? "A" : "B";
  }

  // --- Similarity metrics (pure math over the co-rated set) ---
  const meanGap =
    bothRatedCount > 0 ? bothRated.reduce((s, d) => s + d.gap, 0) / bothRatedCount : 0;
  const tasteMatch = bothRatedCount > 0 ? Math.round((1 - meanGap / 4.5) * 100) : 0;
  const withinHalf = bothRated.filter((d) => d.gap <= 0.5).length;
  const agreementRate = bothRatedCount > 0 ? Math.round((withinHalf / bothRatedCount) * 100) : 0;
  const r = pearson(bothRated.map((d) => [d.ratingA, d.ratingB] as [number, number]));
  const rankingSync = r == null ? null : Math.round(((r + 1) / 2) * 100);
  const overlap = Math.round(jaccard * 100);

  return {
    score: clampedScore,
    tier: tierFor(clampedScore),
    metrics: { tasteMatch, rankingSync, agreementRate, overlap },
    earlyRead,
    counts: {
      bothWatched: bothWatchedCount,
      bothRated: bothRatedCount,
      bothWatchlisted: bothWatchlistedCount,
      unionWatched,
      onlyAWatched,
      onlyBWatched,
    },
    watchTonight,
    sharedCanon,
    disputes,
    crossRecsAToB,
    crossRecsBToA,
    averageA,
    averageB,
    harsherCritic,
  };
}

function cmpKey(x: { uri: string }, y: { uri: string }): number {
  return x.uri < y.uri ? -1 : x.uri > y.uri ? 1 : 0;
}

/**
 * Pearson correlation of paired ratings, or null when undefined (fewer than 3
 * pairs, or zero variance on either side — e.g. someone who rates everything 4).
 */
function pearson(pairs: [number, number][]): number | null {
  const n = pairs.length;
  if (n < 3) return null;
  let sx = 0,
    sy = 0,
    sxx = 0,
    syy = 0,
    sxy = 0;
  for (const [x, y] of pairs) {
    sx += x;
    sy += y;
    sxx += x * x;
    syy += y * y;
    sxy += x * y;
  }
  const num = n * sxy - sx * sy;
  const den = Math.sqrt((n * sxx - sx * sx) * (n * syy - sy * sy));
  if (den === 0) return null;
  return Math.max(-1, Math.min(1, num / den));
}

const titleKey = (e: FilmEntry) => `${(e.t || "").trim().toLowerCase()}|${e.y ?? ""}`;

/** Films `from` rated >= 4 that `to` has not watched (§5.3.6). */
function crossRecs(
  from: Record<string, FilmEntry>,
  to: Record<string, FilmEntry>,
): CrossRecItem[] {
  // Also index `to`'s watched films by normalized title|year. Letterboxd exports
  // sometimes key the same film differently (boxd.it URI vs a missing-URI
  // fallback), so a pure URI match can miss films the other has actually seen
  // (e.g. "Dune: Part Two"). Matching title+year too closes that gap.
  const toWatchedTitles = new Set<string>();
  for (const e of Object.values(to)) if (e.s.includes("w")) toWatchedTitles.add(titleKey(e));

  const out: CrossRecItem[] = [];
  for (const [uri, f] of Object.entries(from)) {
    if (!rated(f) || (f.r as number) < 4) continue;
    const other = to[uri];
    if (other && other.s.includes("w")) continue; // already watched (same key)
    if (toWatchedTitles.has(titleKey(f))) continue; // watched under a different key
    out.push({
      uri,
      title: f.t,
      year: f.y,
      rating: f.r as number,
      onOthersWatchlist: !!other && other.s.includes("l"),
    });
  }
  out.sort((x, y) => y.rating - x.rating || cmpKey(x, y));
  return out;
}
